import { Icon, Input } from '@aqarmap/aqarmap-ui';
import React, { useEffect } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { addListingDetailsType, ListingDetailsType } from '~addListing/types';
import {
  PAYMENT_METHOD_CASH_AND_INSTALLMENT_KEY,
  PAYMENT_METHOD_INSTALLMENT_KEY,
} from '~addListing/utils/data';
import DownPaymentLabel from '~addListing/pages/PriceStep/DownPaymentLabel';
import Tooltip from '~/components/Tooltip';
import { useBreakpoint } from '~/hooks';

const DownPaymentInput = ({
  listing,
  hasDownPayment,
  setHasDownPayment,
}: {
  listing: ListingDetailsType;
  hasDownPayment: boolean;
  setHasDownPayment: (value: boolean) => void;
}) => {
  const { t } = useTranslation('app');
  const { mobileScreen } = useBreakpoint();
  const defaultDownPayment = listing?.payment_plans?.[0]?.down_payment || 0;
  const defaultTotalInstallmentPrice = listing?.payment_plans?.[0]?.price || 0;

  const {
    setValue,
    clearErrors,
    formState: { errors },
  } = useFormContext<addListingDetailsType>();

  const downPaymentType = useWatch({ name: 'downPaymentType' }) || 'amount';
  const downPaymentTypeIsAmount = downPaymentType === 'amount';
  const price = useWatch({ name: 'price' }) || 0;
  const paymentMethod = useWatch({ name: 'paymentMethod' });
  const paymentMethodCashAndInstallment =
    paymentMethod === PAYMENT_METHOD_CASH_AND_INSTALLMENT_KEY;
  const paymentMethodInstallment =
    paymentMethod === PAYMENT_METHOD_INSTALLMENT_KEY;
  const paymentPlans = useWatch({ name: 'paymentPlans' });

  const totalInstallmentPrice = paymentMethodCashAndInstallment
    ? paymentPlans?.[0]?.price || 0
    : price;

  const downPaymentValue = paymentPlans?.[0]?.downPayment || 0;
  const overPrice = paymentPlans?.[0]?.overPrice || 0;
  const totalRequiredPriceForBuyer = downPaymentValue + overPrice;

  const downPaymentAmount =
    downPaymentType === 'amount'
      ? downPaymentValue || 0
      : totalInstallmentPrice * ((downPaymentValue || 0) / 100);

  const downPaymentPercentage =
    downPaymentType === 'percentage'
      ? downPaymentValue || 0
      : totalInstallmentPrice
        ? ((downPaymentValue || 0) / totalInstallmentPrice) * 100
        : 0;

  const handleChangeValue = (value: number) => {
    setValue('paymentPlans.0.downPayment', value, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });
  };

  useEffect(() => {
    setTimeout(() => {
      handleChangeValue(defaultDownPayment);
    }, 0);
  }, [defaultDownPayment]);

  const handleToggleDownPayment: React.ChangeEventHandler<HTMLInputElement> = (
    e,
  ) => {
    const isOpen = e.target.checked;
    setHasDownPayment(isOpen);
    if (isOpen) {
      handleChangeValue(
        downPaymentTypeIsAmount
          ? defaultDownPayment
          : defaultTotalInstallmentPrice
            ? Math.round(
                (defaultDownPayment / defaultTotalInstallmentPrice) * 100,
              )
            : 0,
      );
    } else {
      handleChangeValue(0);
      clearErrors('paymentPlans.0.downPayment');
    }
  };

  const labelComponent = (
    <DownPaymentLabel
      handleChangeValue={handleChangeValue}
      downPaymentTypeIsAmount={downPaymentTypeIsAmount}
      defaultDownPayment={defaultDownPayment}
      defaultTotalInstallmentPrice={defaultTotalInstallmentPrice}
      hasDownPayment={hasDownPayment}
      onToggleDownPayment={handleToggleDownPayment}
    />
  );

  if (!hasDownPayment && !paymentMethodInstallment) {
    return <div className="flex flex-col gap-[10px]">{labelComponent}</div>;
  }

  return (
    <div className="flex flex-col gap-[10px]">
      <Input
        field={{
          type: 'text',
          placeholder: paymentMethodInstallment
            ? t('paidAmount')
            : downPaymentTypeIsAmount
              ? t('downPaymentPricePlaceholder')
              : t('downPaymentPercentagePlaceholder'),
          onChange: (e) => {
            handleChangeValue(Number(e.target.value.split(',').join('')) ?? '');
          },
          onFocus: () => clearErrors('paymentPlans.0.downPayment'),
          value: downPaymentValue ? downPaymentValue?.toLocaleString() : '',
        }}
        label={{
          component: labelComponent,
        }}
        endIcon={
          <p className="border-s ps-x border-gray text-body_2 text-gray__dark_1">
            {downPaymentTypeIsAmount
              ? mobileScreen
                ? t('pound')
                : t('egyptianPound')
              : '%'}
          </p>
        }
        helper={{
          component: Boolean(downPaymentValue) && !paymentMethodInstallment && (
            <p className="flex gap-1 items-center">
              <span className="text-body_2 text-gray__dark_1">
                {downPaymentTypeIsAmount
                  ? t('listingPercentage')
                  : t('downPaymentPrice')}
                :
              </span>
              <span className="text-link text-gray__dark_2" dir="rtl">
                {downPaymentTypeIsAmount
                  ? `${downPaymentPercentage.toLocaleString(undefined, { maximumFractionDigits: 2 })}%`
                  : Math.round(Number(downPaymentAmount)).toLocaleString()}
              </span>
              <span className="text-body_2 text-gray__dark_1">
                {downPaymentTypeIsAmount ? '' : t('abbreviationEgyptianPound')}
              </span>
            </p>
          ),
        }}
        error={
          errors.paymentPlans?.[0]?.downPayment
            ? (() => {
                const msg = errors.paymentPlans?.[0]?.downPayment?.message;
                if (msg === 'downPaymentMustBeLessThanTotalInstallmentPrice') {
                  return t('downPaymentMustBeLessThanTotalInstallmentPrice');
                }
                if (msg === 'downPaymentMustBeAtLeastOnePercent') {
                  return t('downPaymentMustBeAtLeastOnePercent');
                }
                if (downPaymentTypeIsAmount) {
                  return paymentMethodInstallment
                    ? t('paidAmountRequired')
                    : t('downPaymentRequired');
                }
                return t('downPaymentPercentageRequired');
              })()
            : ''
        }
      />
      {paymentMethodInstallment &&
      totalRequiredPriceForBuyer &&
      Boolean(downPaymentValue) ? (
        <p className="text-orange_dark_1 text-body-2 inline-flex gap-x items-center">
          <span className="w-max">
            {t('totalRequiredDownPaymentFromBuyer')}:{' '}
            {totalRequiredPriceForBuyer.toLocaleString()} {t('pound')}
          </span>
          <Tooltip
            text={t('totalRequiredDownPaymentTooltip')}
            className="max-w-[272px]"
            id="down-payment-price-tooltip"
            children={
              <Icon name="info" size="16px" className="text-gray__dark_2" />
            }
          />
        </p>
      ) : null}
    </div>
  );
};

export default DownPaymentInput;
